import { Radio } from 'lucide-react';
import { useNowMs } from '@/hooks/useNowMs';

export interface LiveBadgeProps {
  /**
   * Kick-off time (ISO) an elapsed `HH:MM:SS` clock ticks from. Omit/null for
   * the plain badge — the Figma live card shows just "Live" on binary cards;
   * only the sports moneyline card carries the clock.
   */
  startTime?: string | null;
}

/** Milliseconds → `HH:MM:SS` (elapsed, clamped at 0). */
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/** The ticking clock, isolated so the plain badge never mounts an interval. */
function ElapsedClock({ startMs }: { startMs: number }) {
  const now = useNowMs();
  return <span className="tabular-nums">{formatElapsed(now - startMs)}</span>;
}

/**
 * The event-card "Live" state (ARC-80 §4 + Live page): a pulsing broadcast
 * icon, the word "Live", and — when `startTime` is given — a clock counting up
 * from kick-off. Rendered in the card's subtitle slot in place of the date,
 * on every surface the standard card appears (lobby, trending, Live page).
 */
export function LiveBadge({ startTime }: LiveBadgeProps) {
  const startMs = startTime ? new Date(startTime).getTime() : NaN;
  const hasClock = !Number.isNaN(startMs);

  return (
    <span className="flex items-center gap-1.5 text-sm font-medium text-danger-alt">
      <Radio className="size-4 animate-pulse" aria-hidden />
      <span>Live</span>
      {hasClock && <ElapsedClock startMs={startMs} />}
    </span>
  );
}
